<template>
  <v-container>
    <v-skeleton-loader
      :loading="skeleton"
      type="heading, list-item-three-line"
    >
      <v-row>
        <v-col cols="10">
          <v-icon> event_note </v-icon>
          <span class="headline mx-2">Skill Set</span>
        </v-col>
        <v-col 
          v-show="$hasPermissionTo(['assign skills section']) && !$store.state.isMyProfile"
          cols="2"
        >
          <employee-skills-dialog-create           
            :employee-uid="employeeUid"
            @added="loadEmployeeSkills(); updated()"
          />
        </v-col>
      </v-row>
    
      <v-row 
        v-if="noOfSkills > 0"
      >
        <v-col>
          <template 
            v-for="(employeeSkill, i) in filteredSkills"          
          >
            <v-hover :key="employeeSkill.skill_id">
              <v-row               
                slot-scope="{ hover }"
              >             
                <v-col
                  cols="12"
                  class="py-0"
                >
                  <v-list
                    three-line
                    class="py-1"
                  >
                    <v-list-item>
                      <v-list-item-avatar tile>
                        <img :src="employeeSkill.skill.image_url">   
                      </v-list-item-avatar>
                      <v-list-item-content>
                        <v-list-item-title 
                          style="max-width: 400px;" 
                          class="font-weight-medium text-capitalize text-truncate pl-2"
                        >
                          {{ employeeSkill.skill.name }}
                        </v-list-item-title>
                        <v-list-item-subtitle>
                          <v-rating
                            v-if="!edit[i]"
                            :value="employeeSkill.skill_level.level"
                            :length="employeeSkill.skill.levels.length"
                            readonly
                          >
                            <template v-slot:item="props">
                              <v-tooltip
                                max-width="350px"
                                content-class="bottom"
                                bottom
                              >
                                <template v-slot:activator="{ on }">
                                  <v-icon
                                    small
                                    background-color="purple lighten-3"
                                    color="purple"
                                    v-on="on"
                                  >
                                    {{ props.isFilled ? 'mdi-star' : 'mdi-star-outline' }}
                                  </v-icon>
                                </template>
                                <span> {{ employeeSkill.skill.levels[props.index].description }}</span>
                              </v-tooltip>
                            </template>
                          </v-rating>                                                
                          <v-rating
                            v-else
                            v-model="rating[i]"
                            :value="employeeSkill.skill_level.level"
                            :length="employeeSkill.skill.levels.length"
                          >
                            <template v-slot:item="props">
                              <v-icon
                                :color="props.isFilled ? genColor(props.index) : 'grey lighten-1'"
                                @click="props.click"
                              >
                                {{ props.isFilled ? 'mdi-star-circle' : 'mdi-star' }}
                              </v-icon>
                            </template>
                          </v-rating>   
                        
                          <span 
                            v-show="!edit[i]" 
                            class="caption ml-1"
                          >
                            {{ $moment(employeeSkill.updated_at).format('MMM D, YYYY') }}
                          </span>  
                          <v-tooltip
                            v-if="!edit[i]"
                            max-width="350px"
                            content-class="bottom"
                            bottom
                          >
                            <template v-slot:activator="{ on }">
                              <v-icon
                                small
                                v-on="on"
                              >
                                mdi-account
                              </v-icon>
                            </template>
                            <span> {{ `Given by ${employeeSkill.updated_by}` }}</span>
                          </v-tooltip>             
                        </v-list-item-subtitle>
                        <v-list-item-subtitle 
                          v-show="!edit[i]" 
                          class="text-justify pl-2"
                        >
                          {{ employeeSkill.skill_level.description }}
                        </v-list-item-subtitle>
                      </v-list-item-content>

                      <v-list-item-action v-if="edit[i]">
                        <v-btn
                          class="my-0"
                          :loading="loading[i]"
                          icon
                          @click="update(employeeSkill.skill.levels[rating[i] - 1].id, employeeSkill.skill_level.id, i)"
                        >
                          <v-icon color="secondary">
                            check
                          </v-icon>
                        </v-btn>
                        <v-btn
                          class="my-0"
                          :disabled="loading[i]"
                          icon
                          @click="cancelEdit(i)"
                        >
                          <v-icon color="error">
                            cancel
                          </v-icon>
                        </v-btn>
                      </v-list-item-action>
                      <v-list-item-action v-if="canEdit()">                      
                        <v-menu                         
                          bottom
                          right
                        >
                          <template v-slot:activator="{ on }">
                            <v-btn
                              v-show="hover && !edit[i]"
                              icon
                              v-on="on"
                            >
                              <v-icon>
                                more_vert
                              </v-icon>
                            </v-btn>
                          </template>

                          <v-list>
                            <v-list-item 
                              v-show="$hasPermissionTo(['assign skills section'])" 
                              @click="editLevel(i, employeeSkill.skill_level.level)"
                            >
                              <v-list-item-title> Edit </v-list-item-title>
                            </v-list-item>
                            <v-list-item 
                              v-show="$hasPermissionTo(['delete employee skills'])"
                              @click="$refs.deleteDialog.toggleDialog(employeeSkill.skill)"
                            >
                              <v-list-item-title> Remove </v-list-item-title>
                            </v-list-item>
                          </v-list>
                        </v-menu>                    
                      </v-list-item-action>
                    </v-list-item>
                  </v-list>
                  <v-data-table
                    v-show="edit[i]"
                    :headers="headers"
                    :items="employeeSkill.skill.levels"
                    :sort-by.sync="sortBy"
                    hide-default-footer
                    class="elevation-1"
                  >
                    <template v-slot:item="props">
                      <tr 
                        :class="{active: props.item.level === rating[i]}"
                      >
                        <td class="text-right">
                          {{ props.item.level }}
                        </td>
                        <td class="text-justify">
                          {{ props.item.description }}
                        </td>
                      </tr>
                    </template>
                  </v-data-table>
                </v-col>
              </v-row>
            </v-hover>
          </template>
        </v-col>
      </v-row>
      <v-row 
        v-else 
      >
        <v-col cols="12">
          <span class="mx-2"> No data available </span>
        </v-col>
      </v-row>
      <div v-if="noOfSkills >= 4">
        <a 
          v-if="filter" 
          @click.stop="filter = false"
        >Show more ...</a> 
        <a 
          v-else 
          @click.stop="filter = true"
        >Show less ...</a>
      </div>
      <employee-skills-dialog-delete 
        ref="deleteDialog" 
        :employee-uid="employeeUid" 
        @deleted="loadEmployeeSkills(); updated()"
      />
    </v-skeleton-loader>
  </v-container>
</template>

<script>
  import { mapState } from 'vuex'

  export default {
    components: {
      EmployeeSkillsDialogCreate: () => import('@/components/employee/skills/DialogCreate'),
      EmployeeSkillsDialogDelete: () => import('@/components/employee/skills/DialogDelete'),
    },
    props: {
      employeeUid: {
        type: String,
        default: '',
      },    
    },
    data () {
      return {
        headers: [
          {
            text: 'Level',
            value: 'level',
            sortable: false, 
          },
          { 
            text: 'Description', 
            align: 'left', 
            value: 'description',
            sortable: false, 
          },
        ],
        sortBy: 'level',
        colors: ['error', 'warning', 'indigo', 'success', 'primary'],
        filter: true,
        skeleton: true,
        loading: [],
        rating: [],
        edit: [],
        employeeSkills: [],
        operators: {
          'asc': (levelA, levelB) => {
            return levelA - levelB 
          },
          'desc': (levelA, levelB) => {
            return levelB - levelA 
          },
        },
      }
    },
    computed: {    
      ...mapState(['currentEmployee']),
      filteredSkills () {
        if (this.noOfSkills < 4) return this.sortedSkills

        return this.filter ? this.sortedSkills.slice(0, 3) : this.sortedSkills
      },
      sortedSkills () {
        return [...this.employeeSkills]
          .sort((a, b) => this.operators['desc'](a.skill_level.level, b.skill_level.level))
          .sort((a, b) => this.operators['desc'](a.skill.levels.length, b.skill.levels.length))
      },
      noOfSkills () {
        return this.employeeSkills.length
      },
    },
    watch: {
      employeeUid: {
        immediate: true,
        handler (value) {
          if (value) {
            this.$resetData()
            this.loadEmployeeSkills()
          }
        },
      },
    },
    methods: {
      loadEmployeeSkills () {
        this.$api.employeeSkills().list(this.employeeUid)
          .then(response => {
            this.employeeSkills = response.data
            this.skeleton = false     
          })
      },
      updated () {
        this.$emit('updated')
      },
      update (newLevelId, originalLevelId, index) {
        if (newLevelId === originalLevelId || !newLevelId) {
          this.cancelEdit(index)
          return
        }

        this.$set(this.loading, index, true)

        this.$api.employeeSkills().update(this.employeeUid, newLevelId)
          .then(response => {
            if (response.status >= 200 && response.status < 300) {
              this.$snack({ message: response.data.status })
              this.loadEmployeeSkills()

              this.updated()
              this.cancelEdit(index)
            }
          })
          .catch(() => {})
          .then(() => {
            this.$set(this.loading, index, false)
          })
      },
      editLevel (i, level) {
        this.$set(this.edit, i, true)

        this.$set(this.rating, i, level)
      },
      cancelEdit (i) {
        this.$set(this.edit, i, false)
      
        this.$set(this.rating, i, null)
      },
      canEdit () {
        return (this.$hasPermissionTo(['assign skills section']) || this.$hasPermissionTo(['delete employee skills'])) && !this.$store.state.isMyProfile
      },
      genColor (i) {
        return this.colors[i]
      },
    },
  }
</script>

<style scoped>
.v-rating {
  display: inline;
}
</style>
